import type { ChatMessages } from "@storytell/cp-client";
import { TbChevronRight, TbGlobe, TbExternalLink } from "solid-icons/tb";
import { For, Show, createSignal } from "solid-js";

interface WebSearchResultsRendererProps {
  webSearchResults: ChatMessages.WebSearchResult;
}

interface WebSearchResultItemProps {
  result: ChatMessages.WebSearchResultItem;
  index: number;
}

const WebSearchResultItem = (props: WebSearchResultItemProps) => {
  return (
    <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      <div class="flex items-start gap-3">
        {/* Favicon */}
        <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          <Show when={props.result.faviconUrl} fallback={<TbGlobe class="w-4 h-4 text-blue-600 dark:text-blue-400" />}>
            <img
              src={props.result.faviconUrl}
              alt={`${props.result.title} favicon`}
              class="w-4 h-4 object-contain"
              onError={(e) => {
                // If favicon fails to load, show globe icon instead
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const parent = target.parentElement;
                if (parent) {
                  const globeIcon = document.createElement("div");
                  globeIcon.innerHTML =
                    '<svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="m5 12c0-4.4 3.6-8 8-8s8 3.6 8 8-3.6 8-8 8-8-3.6-8-8z"></path></svg>';
                  parent.appendChild(globeIcon.firstChild!);
                }
              }}
            />
          </Show>
        </div>

        <div class="flex-1 min-w-0">
          {/* Title with external link */}
          <div class="flex items-center gap-2 mb-1">
            <a
              href={props.result.url}
              target="_blank"
              rel="noopener noreferrer"
              class="font-medium text-sm text-blue-600 dark:text-blue-400 hover:underline truncate flex-1 group"
            >
              {props.result.title}
              <TbExternalLink class="inline w-3 h-3 ml-1 opacity-60 group-hover:opacity-100" />
            </a>
            <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded flex-shrink-0">
              #{props.index + 1}
            </span>
          </div>

          {/* URL */}
          <div class="text-xs text-green-600 dark:text-green-400 mb-2 truncate">{props.result.url}</div>

          {/* Text summary */}
          <Show when={props.result.text}>
            <p class="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-3">
              {/* Truncate to approximately 200 characters */}
              {props.result.text.length > 200 ? `${props.result.text.substring(0, 200)}...` : props.result.text}
            </p>
          </Show>

          {/* Metadata */}
          <div class="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <Show when={props.result.publishedDate}>
              <span>Published: {props.result.publishedDate}</span>
            </Show>
            <Show when={props.result.author}>
              <span>By: {props.result.author}</span>
            </Show>
            <Show when={props.result.score > 0}>
              <span>Relevance: {Math.round(props.result.score * 100)}%</span>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders web search results in a structured format with favicons, clickable titles,
 * and text summaries instead of markdown
 */
export const WebSearchResultsRenderer = (props: WebSearchResultsRendererProps) => {
  const duration = () => {
    const duration = props.webSearchResults.duration;
    if (!duration) return "0 seconds";

    let totalSeconds: number;
    if (duration.seconds !== undefined) {
      totalSeconds = Number(duration.seconds) + Number(duration.nanos || 0) / 1000000000;
    } else {
      const durationNum = Number(duration);
      totalSeconds = !Number.isNaN(durationNum) ? durationNum : 0;
    }
    // Round to nearest 0.1
    const rounded = Math.round(totalSeconds * 10) / 10;
    const label = rounded === 1 ? "second" : "seconds";
    return `${rounded} ${label}`;
  };

  const [showResults, setShowResults] = createSignal(true); // Start expanded for better UX

  return (
    <div>
      <div class="flex items-center gap-1 mb-2">
        <TbGlobe class="size-4 text-muted-text" />
        <h3 class="text-sm text-secondary-text">
          Web search for <span class="font-medium text-primary-text">"{props.webSearchResults.query}"</span>
        </h3>
      </div>

      <Show
        when={props.webSearchResults.results && props.webSearchResults.results.length > 0}
        fallback={<p class="text-sm text-primary-text italic">No web search results found</p>}
      >
        <button
          type="button"
          onClick={() => setShowResults((prev) => !prev)}
          class="group/btn flex items-center gap-2 mb-2 cursor-pointer"
        >
          <p class="text-sm text-blue-800 dark:text-blue-400 font-medium group-hover/btn:underline">
            Found {props.webSearchResults.results.length} result
            {props.webSearchResults.results.length !== 1 ? "s" : ""} in {duration()}
          </p>

          <TbChevronRight
            class="size-3.5 stroke-3 text-secondary-text transition-transform"
            classList={{ "rotate-90": showResults() }}
          />
        </button>

        <Show when={showResults()}>
          <div class="space-y-3">
            <For each={props.webSearchResults.results}>
              {(result, index) => <WebSearchResultItem result={result} index={index()} />}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};
